/**
 * Reference cells: a record id ("11x17186", "19x1", a spine id) shown as the referenced record's
 * name, linked to its page when it is a kit module. Names come from the gateway's reference cache
 * (organisations, people, users…), then from the kernel record's label; the id is the last resort.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Select } from "antd";
import { kernel } from "../api";
import { resourceBySlug } from "../kit-resources";

const names = new Map<string, string>();
const pending = new Map<string, Promise<string>>();
let cache: Promise<Record<string, string>> | null = null;
let refreshed = false;

const loadCache = (refresh = false): Promise<Record<string, string>> =>
  (refresh ? fetch("/api/schema/references/refresh", { method: "POST", credentials: "include" }).catch(() => undefined) : Promise.resolve())
    .then(() => fetch("/api/schema/references", { credentials: "include" }))
    .then((r) => (r.ok ? r.json() : { references: {} }))
    .then((d: { references?: Record<string, string> }) => d.references ?? {})
    .catch(() => ({}));

async function resolve(id: string): Promise<string> {
  cache ??= loadCache();
  let refs = await cache;
  // the gateway fills its cache lazily: refresh it once per session on the first miss
  if (!refs[id] && /^\d+x\d+$/.test(id) && !refreshed) {
    refreshed = true;
    cache = loadCache(true);
    refs = await cache;
  }
  if (refs[id]) return refs[id];
  const e = await kernel<{ label?: string }>(`/entities/${encodeURIComponent(id)}`).catch(() => undefined);
  return e?.label || id;
}

export function nameOf(id: string): Promise<string> {
  const known = names.get(id);
  if (known) return Promise.resolve(known);
  let p = pending.get(id);
  if (!p) {
    p = resolve(id).then((n) => { names.set(id, n); return n; });
    pending.set(id, p);
  }
  return p;
}

export const RefCell: React.FC<{ id: string; refTo?: string }> = ({ id, refTo }) => {
  const [name, setName] = useState(names.get(id) ?? "…");
  useEffect(() => { let live = true; nameOf(id).then((n) => live && setName(n)); return () => { live = false; }; }, [id]);
  return refTo && resourceBySlug[refTo] ? <Link to={`/${refTo}/show/${encodeURIComponent(id)}`}>{name}</Link> : <span>{name}</span>;
};

/**
 * Picker for a reference field (never a text input: UI contract rule 15). Candidates by target:
 * users / organisations / people from the gateway's reference cache (coreBOS standard id prefixes),
 * kit modules from the kernel, other coreBOS modules (Products) through the coreBOS provider.
 */
const BASE_PREFIX: Record<string, string> = { user: "19x", account: "11x", contact: "12x" };
const COREBOS_TARGETS: Record<string, { module: string; label: string }> = { product: { module: "Products", label: "productname" } };
// search text reaches a coreBOS query: keep it to letters, digits and a little punctuation
const safe = (q: string) => q.replace(/[^\p{L}\p{N} ._-]/gu, "").slice(0, 40);

async function candidates(refTo: string, q: string): Promise<Array<{ value: string; label: string }>> {
  const prefix = BASE_PREFIX[refTo];
  if (prefix) {
    cache ??= loadCache();
    let refs = await cache;
    if (!Object.keys(refs).some((k) => k.startsWith(prefix)) && !refreshed) { refreshed = true; cache = loadCache(true); refs = await cache; }
    const needle = q.toLowerCase();
    return Object.entries(refs).filter(([id, n]) => id.startsWith(prefix) && (!needle || n.toLowerCase().includes(needle)))
      .slice(0, 50).map(([id, n]) => ({ value: id, label: n }));
  }
  const cb = COREBOS_TARGETS[refTo];
  if (cb) {
    const { dataProvider } = await import("../dataProviders");
    const r = await dataProvider.corebos.getList({ resource: cb.module, pagination: { current: 1, pageSize: 30 },
      filters: safe(q) ? [{ field: cb.label, operator: "contains", value: safe(q) }] : [] }).catch(() => ({ data: [] }));
    return (r.data as Array<Record<string, unknown>>).map((x) => ({ value: String(x.id), label: String(x[cb.label] ?? x.id) }));
  }
  const r = await kernel<{ entities: Array<{ entity_id: string; label: string }> }>(
    `/entities?type=${encodeURIComponent(refTo)}&limit=50${q ? `&search=${encodeURIComponent(q)}` : ""}`).catch(() => ({ entities: [] }));
  return r.entities.map((e) => ({ value: e.entity_id, label: e.label || e.entity_id }));
}

export const RefSelect: React.FC<{ refTo?: string; value?: string; onChange?: (v: string | undefined) => void }> = ({ refTo, value, onChange }) => {
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<string>();
  const search = (q: string) => { if (!refTo) return; setLoading(true); candidates(refTo, q).then(setOptions).finally(() => setLoading(false)); };
  useEffect(() => { search(""); }, [refTo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (value) nameOf(String(value)).then(setCurrent); }, [value]);
  const all = value && !options.some((o) => o.value === value) ? [{ value: String(value), label: current ?? String(value) }, ...options] : options;
  return <Select showSearch allowClear value={value || undefined} onChange={(v) => onChange?.(v)} options={all} loading={loading}
    filterOption={false} onSearch={search} placeholder="Cerca…" notFoundContent={loading ? "Caricamento…" : "Nessun risultato"} />;
};
